// src/components/EditableField.tsx
import React, { useState, useRef, useEffect } from 'react';
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';

interface EditableFieldProps {
    label: string;
    value: string | number | null | undefined;
    field: string; // The key of the field in the customer object
    onUpdate: (field: string, value: string | null) => Promise<void>; // Async update function
    required?: boolean;
    inputType?: 'text' | 'email' | 'tel' | 'number' | 'date';
    reminder?: string | null; // Reminder prop
    suggestion?: string; // Add this

}

export const EditableField: React.FC<EditableFieldProps> = ({
    label,
    value,
    field,
    onUpdate,
    required = false,
    inputType = 'text',
    reminder = null,
    suggestion,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value ?? '');
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const editContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isEditing) {
            setCurrentValue(value ?? '');
        }
    }, [value, isEditing]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            if (inputType === 'text' || inputType === 'email' || inputType === 'tel' || inputType === 'number') {
                inputRef.current.select();
            }
        }
    }, [isEditing, inputType]);

    const handleEditClick = () => {
        setCurrentValue(value ?? '');
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        if (isLoading) return;

        const trimmedValue = String(currentValue).trim();

        if (required && !trimmedValue) {
            console.error(`Field "${label}" is required.`);
            toast.warn(`Field "${label}" is required.`, { toastId: `required-${field}` });
            // inputRef.current?.focus();
            return;
        }

        const originalValueStr = value != null ? String(value) : '';
        if (trimmedValue !== originalValueStr) {
            setIsLoading(true);
            try {
                // Call onUpdate - this will update the state in CustomerProfileModal,
                // including the phoneReminder state based on the new value.
                await onUpdate(field, trimmedValue === '' && !required ? null : trimmedValue);
                // Exit edit mode *after* the update is processed by the parent
                setIsEditing(false);
            } catch (error) {
                console.error(`Update failed for ${field}:`, error);
                toast.error(`Failed to update ${label}.`);
                // Keep editing mode open on error
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsEditing(false); // Exit if no change
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentValue(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSaveClick();
        } else if (e.key === 'Escape') {
            handleCancelClick();
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (!editContainerRef.current?.contains(event.relatedTarget as Node | null)) {
            // Use a small timeout to allow potential state updates from onUpdate to settle
            // before checking the reminder state again, although ideally the parent handles it.
            setTimeout(() => {
                handleSaveClick();
            }, 100); // Small delay might help, but parent logic is key
        }
    };

    return (
        // Add padding-bottom to the container to make space for the reminder
        <div className="flex flex-col items-start my-1 min-h-[28px] relative group pb-4"> {/* Increased padding-bottom */}
            <div className='w-full'><strong className="w-28 inline-block flex-shrink-0 text-sm pt-1.5"> {/* Added padding-top for alignment */}
                {label}:{required && <span className="text-red-500 ml-1">*</span>}
            </strong></div>
            <div className="flex-grow relative w-full"> {/* Added relative positioning here */}
                {isEditing ? (
                    <div ref={editContainerRef} className="flex items-center"> {/* Removed my-2 */}
                        <div className="relative flex-grow"> {/* Input wrapper */}
                            <input
                                ref={inputRef}
                                type={inputType}
                                value={currentValue}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onBlur={handleBlur}
                                className={`border px-1 py-1.5 text-sm w-full rounded flex-grow mr-1 ${required && !String(currentValue).trim() ? 'border-red-500' : 'border-gray-300'}`}
                                disabled={isLoading}
                            />
                            {/* Reminder is now outside the input wrapper, positioned relative to flex-grow div */}
                            {/* Show suggestion button if input is empty and suggestion exists */}
                            {suggestion && (
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-500 underline"
                                    onClick={() => setCurrentValue(suggestion)}
                                    tabIndex={-1}
                                >
                                    Use suggestion
                                </button>
                            )}
                        </div>
                        <button
                            onClick={handleSaveClick}
                            className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50 ml-1"
                            disabled={isLoading || (required && !String(currentValue).trim())}
                            aria-label="Save"
                        >
                            {isLoading ? '...' : <FiCheck size={16} />}
                        </button>
                        <button
                            onClick={handleCancelClick}
                            className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50"
                            disabled={isLoading}
                            aria-label="Cancel"
                        >
                            <FiX size={16} />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-between cursor-pointer" onClick={handleEditClick}> {/* Removed m-1 */}
                        <span className="text-sm px-1 py-1.5 flex-grow mr-1 truncate" title={String(value ?? '')}>
                            {value !== undefined && value !== null && value !== ''
                                ? value
                                : (
                                    <span className="text-gray-400 italic">
                                        Not set
                                        {suggestion && (
                                            <> (suggestion: {suggestion})</>
                                        )}
                                    </span>
                                )
                            }
                        </span>
                        <button
                            tabIndex={-1}
                            className="text-blue-500 hover:text-blue-700 p-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                            aria-label={`Edit ${label}`}
                        >
                            <FiEdit2 size={14} />
                        </button>
                    </div>
                )}
                {/* --- Display Reminder (always positioned absolutely below the field area) --- */}
                {reminder && (
                    <p className="absolute top-full left-0 text-xs text-red-600 mt-0.5 z-10 w-full"> {/* Ensure it takes full width if needed */}
                        {reminder}
                    </p>
                )}
                {/* --- End Reminder Display --- */}
            </div>
        </div>
    );
};
